/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AfterViewInit, Directive, ElementRef, EventEmitter, HostListener, Output } from '@angular/core';
import { LogService } from '../../services';

@Directive({
    selector: '[appDraggable]',
})
export class DraggableDirective implements AfterViewInit {
    /**
     * Mouse X offset position.
     * @private
     */
    private mouseXOffset: number = 0;

    /**
     * Mouse Y offset position.
     * @private
     */
    private mouseYOffset: number = 0;

    /**
     * Mouse down X position.
     * @private
     */
    private mouseDownX: number = 0;

    /**
     * Mouse down Y position.
     * @private
     */
    private mouseDownY: number = 0;

    /**
     * Flag to determine if drag should start.
     * @private
     */
    private isDraggable: boolean = false;

    /**
     * Draggable element.
     * @private
     */
    private element: HTMLElement;

    @Output() public dragActiveEmitter = new EventEmitter();

    constructor(
        private elementRef: ElementRef,
        private logService: LogService
    ) {
    }

    ngAfterViewInit(): void {
        const element = this.elementRef.nativeElement;
        this.element = element;
        const dragHandle = element.querySelector('#drag-handle');
        dragHandle.style.cursor = 'move';
    }

    @HostListener('mousedown', ['$event'])
    public onMouseDown(event: MouseEvent): void {
        if (this.searchForDragHandle(event)) {
            event.preventDefault();
            this.isDraggable = true;
            this.mouseDownX = event.clientX;
            this.mouseDownY = event.clientY;
            this.dragActiveEmitter.next(true);
        }
    }

    @HostListener('mouseup', ['$event'])
    public onMouseUp(): void {
        this.isDraggable = false;
        this.dragActiveEmitter.next(false);
    }

    @HostListener('mousemove', ['$event'])
    public onMouseMove(event: MouseEvent): void {
        if (this.isDraggable) {
            event.preventDefault();
            this.mouseXOffset = this.mouseDownX - event.clientX;
            this.mouseYOffset = this.mouseDownY - event.clientY;
            this.mouseDownX = event.clientX;
            this.mouseDownY = event.clientY;
            this.element.style.top = (this.element.offsetTop - this.mouseYOffset) + 'px';
            this.element.style.left = (this.element.offsetLeft - this.mouseXOffset) + 'px';
        }
    }

    @HostListener('mouseleave', ['$event'])
    public onMouseLeave() {
        this.isDraggable = false;
        this.dragActiveEmitter.next(false);
    }

    @HostListener('window:resize', ['$event'])
    public resetElementPosition(): void {
        this.element.style.top = '50%';
        this.element.style.left = '50%';
        this.element.style.transform = 'translateX(-50%) translateY(-50%)';
    }

    /**
     * Search for drag handle.
     * @private
     */
    private searchForDragHandle(event: any): boolean {
        if (event.target.id === 'drag-handle') {
            return true;
        } else {
            this.logService.debug('DraggableDirective: DraggableDirective', 'No drag handle found');
        }
    }
}
